import React from 'react';
import clsx from 'clsx';
import { Cell, Row, TableBody, TableHeader, SortDescriptor } from 'react-aria-components';

import { Button } from '@/components/v2/Button';
import { isWithin24Hours, formattedDateAndTime } from '@/utils/utils';
import {
  FetchNextPageOptions,
  InfiniteData,
  InfiniteQueryObserverResult,
} from 'react-query/types/core';
import Toggle from '@oncolens/oncolens-ui/lib/components/Common/Toggle/Toggle';
import {
  Conference,
  activateConference,
  conferenceKeys,
  deactivateConference,
} from '../api/conferences';
import { Table, Column } from '@/components/v2/Table';
import { useQueryClient } from 'react-query';
import { conferenceDisplayName } from '@/utils/conferenceDisplayName';

interface ConferencesTableProps {
  data:
    | InfiniteData<{
        data: Conference[];
        pagination: any;
      }>
    | undefined;
  fetchNextPage: (options?: FetchNextPageOptions | undefined) => Promise<
    InfiniteQueryObserverResult<
      {
        data: Conference[];
        pagination: any;
      },
      unknown
    >
  >;
  hasNextPage: boolean | undefined;
  isFetchingNextPage: boolean;
  handleEditConference: (conference: Conference) => void;
  sortDescriptor?: SortDescriptor;
  onSortChange?: (descriptor: SortDescriptor) => any;
}

export const ConferencesTable = ({
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  handleEditConference,
  sortDescriptor,
  onSortChange,
}: ConferencesTableProps) => {
  const client = useQueryClient();

  const toggleActivation = (id: string, isActive: boolean) =>
    isActive
      ? () => deactivateConference(id).then(() => client.invalidateQueries(conferenceKeys.list()))
      : () => activateConference(id).then(() => client.invalidateQueries(conferenceKeys.list()));

  return (
    <>
      <Table
        sortDescriptor={sortDescriptor}
        onSortChange={onSortChange}
        className="w-full text-xs bg-white text-zinc-800"
        aria-label="Files"
        selectionMode="multiple">
        <TableHeader className="text-left border-b border-zinc-200">
          <Column className="px-2 py-4" isRowHeader data-cy="conference-table-column-active">
            Active
          </Column>
          <Column
            id="name"
            className="px-2 py-4"
            isRowHeader
            allowsSorting
            data-cy="conference-table-column-name">
            Name
          </Column>
          <Column
            id="parentOrganizationName"
            className="px-2"
            allowsSorting
            data-cy="conference-table-column-parentorg">
            Parent org
          </Column>
          <Column
            id="organizationName"
            className="px-2"
            allowsSorting
            data-cy="conference-table-column-hostorg">
            Host org
          </Column>
          <Column
            id="CreatedDate"
            className="px-2"
            allowsSorting
            data-cy="conference-table-column-added-on">
            Added on
          </Column>
        </TableHeader>
        <TableBody
          data-cy="conference-list-table-body"
          renderEmptyState={() => 'No conferences available'}>
          {data?.pages.map((page) =>
            page.data.map((conference) => (
              <Row
                className="border-b even:bg-white odd:bg-zinc-100 border-zinc-200"
                key={conference?.id}>
                <Cell className="text-center">
                  <span className="text-left">
                    <Toggle
                      label=""
                      value={conference.isActive}
                      onChange={toggleActivation(conference.id, conference.isActive)}
                      className="ml-auto text-sm whitespace-nowrap"
                    />
                  </span>
                </Cell>
                <StandardCell>
                  <Button
                    variant="link"
                    onClickHandler={() => handleEditConference(conference)}
                    testId={`${conference?.name}-button`}>
                    <div className="flex items-center gap-1">
                      {isWithin24Hours(conference.addedOn) && (
                        <div className="bg-sky-700 uppercase text-white font-bold rounded-sm px-1.5 py-0.5 text-[10px]">
                          new
                        </div>
                      )}
                      {conferenceDisplayName(conference?.name, conference?.status)}
                    </div>
                  </Button>
                </StandardCell>
                <StandardCell>{conference?.parentOrg}</StandardCell>
                <StandardCell>{conference?.hostOrg}</StandardCell>
                <StandardCell>{formattedDateAndTime(conference?.addedOn)}</StandardCell>
              </Row>
            )),
          )}
        </TableBody>
      </Table>
      {hasNextPage && (
        <div className="flex justify-center w-full py-4">
          <Button
            variant="link"
            onClickHandler={() => fetchNextPage()}
            disabled={!hasNextPage || isFetchingNextPage}
            testId="view-more-conferences-button">
            View next 50 conferences
          </Button>
        </div>
      )}
    </>
  );
};

const StandardCell = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => <Cell className={clsx(className, 'py-6 px-2 w-1/4')}>{children}</Cell>;
